// Billing, through the hub's 2 logged-in functions (docs/data-model.md section 10). Nothing about
// money is stored in the hub: Stripe keeps the cards, invoices and amounts, and the functions read
// them when a screen asks.
// - member-billing: Manage billing and the banner's Update card (a Stripe customer portal session
//   for the member's own customer), and Account's Billing box.
// - stripe-prices: the balance on a secured place's page and its pay button.
import { callHubFunction, HubFunctionError } from './functions.js'

export { HubFunctionError }

// A portal session is only ever opened on Stripe's own billing address
const PORTAL = /^https:\/\/billing\.stripe\.com\//

// Calls a logged-in function with the member's session. The function's own error code, such as
// no_customer or viewing_as, comes back on the thrown error (lib/functions.js).
const invokeHub = callHubFunction

// Opens Stripe's billing page for them in a new tab, already logged in as them, as the Account
// board says. The tab is opened inside the click, so the browser doesn't block it as a pop-up, and
// goes to Stripe once the hub has made the session. flow 'card' opens straight on the card form
// (the payment banner and the paused page's Update card); productId picks that product's billing.
export function openBillingPortal({ productId = null, flow = null } = {}) {
  let tab = null
  try { tab = window.open('', '_blank') } catch { tab = null }
  if (tab) {
    try {
      tab.opener = null
      tab.document.title = 'Opening Stripe'
    } catch { /* another browser's rules; the tab still opens */ }
  }
  const body = { action: 'portal' }
  if (productId) body.product_id = productId
  if (flow) body.flow = flow
  return invokeHub('member-billing', body)
    .then(data => {
      const url = data?.url
      if (typeof url !== 'string' || !PORTAL.test(url)) throw new HubFunctionError('stripe', 502)
      if (tab && !tab.closed) tab.location.replace(url)
      else window.location.assign(url)
    })
    .catch(e => {
      if (tab && !tab.closed) tab.close()
      throw e
    })
}

// Account's Billing box: { has_customer, cancel_off: [{ product_id, name, term_months }],
// next_payment: { product_name, amount, due_on } or null }
export function loadBillingSummary() {
  return invokeHub('member-billing', { action: 'summary' })
}

// The amount of each of their secured places' balances: [{ product_id, balance_price_id, amount, recurring }].
// The Library (Next up, Account's Billing and My programmes) and the place secured page both want them
// as a page opens, so an ask for the same person in the last 30 seconds, or still on its way, is shared
// rather than asking Stripe twice; an ask that failed isn't kept. A page that reloads asks again (after
// paying, Stripe's page sends them back to a fresh load).
const BALANCES_KEEP_MS = 30e3
const balanceAsks = new Map()
export function loadBalances(personId = null) {
  const key = personId || ''
  const kept = balanceAsks.get(key)
  if (kept && Date.now() - kept.at < BALANCES_KEEP_MS) return kept.promise
  const promise = invokeHub('stripe-prices', {}).then(data => (Array.isArray(data?.balances) ? data.balances : []))
  balanceAsks.set(key, { at: Date.now(), promise })
  promise.catch(() => { if (balanceAsks.get(key)?.promise === promise) balanceAsks.delete(key) })
  return promise
}
